import { useEffect } from 'react'

declare global {
  interface Window {
    fbq?: ((...args: unknown[]) => void) & {
      callMethod?: (...args: unknown[]) => void
      queue?: unknown[][]
      push?: unknown
      loaded?: boolean
      version?: string
    }
    _fbq?: Window['fbq']
  }
}

const SCRIPT_ID = 'meta-pixel-script'
const SCRIPT_SRC = 'https://connect.facebook.net/en_US/fbevents.js'

/**
 * Loads the Meta (Facebook) Pixel once and fires the initial PageView.
 */
export function useMetaPixel(pixelId: string) {
  useEffect(() => {
    if (!pixelId) return

    if (!window.fbq) {
      const fbq = function (...args: unknown[]) {
        if (fbq.callMethod) {
          fbq.callMethod.apply(fbq, args)
        } else {
          fbq.queue?.push(args)
        }
      } as NonNullable<Window['fbq']>

      fbq.queue = []
      fbq.loaded = true
      fbq.version = '2.0'
      fbq.push = fbq

      window.fbq = fbq
      if (!window._fbq) window._fbq = fbq
    }

    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement('script')
      script.id = SCRIPT_ID
      script.async = true
      script.src = SCRIPT_SRC
      document.head.appendChild(script)
    }

    if (!document.getElementById('meta-pixel-noscript')) {
      const noscript = document.createElement('noscript')
      noscript.id = 'meta-pixel-noscript'
      const img = document.createElement('img')
      img.height = 1
      img.width = 1
      img.style.display = 'none'
      img.alt = ''
      img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`
      noscript.appendChild(img)
      document.body.appendChild(noscript)
    }

    window.fbq?.('init', pixelId)
    window.fbq?.('track', 'PageView')
  }, [pixelId])
}

/** Fires a standard or custom Meta Pixel event, if the pixel is loaded. */
export function trackPixelEvent(event: string, params?: Record<string, unknown>) {
  window.fbq?.('track', event, params)
}
